import { useEffect, useState } from 'react';
import { Dropdown, Input, Option, Radio, RadioGroup, Textarea } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';

import { MOCK_DOCUMENT_LABELS, MOCK_USER_NAMES } from '../../data';
import { TEMPLATE_FIELD_TYPE, TFieldValue, TTemplateFieldType } from '../../models';
import { DateLabel } from '@shared/components';

const YES_NO_UNKNOWN = ['Yes', 'No', 'Unknown'];

type TProps = {
  id: string;
  controlType: TTemplateFieldType;
  options?: string[];
  value: TFieldValue;
  disabled?: boolean;
  onChange: (value: TFieldValue) => void;
};

const asString = (value: TFieldValue): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return '';
  if (typeof value === 'object') return value.id;
  return String(value);
};

const asStringArray = (value: TFieldValue): string[] => {
  if (Array.isArray(value)) return value.map((entry) => (typeof entry === 'object' ? entry.id : String(entry)));
  return [];
};

// Renders one configured control from its type. The same renderer is used for
// template custom fields and assessment questions - the data models stay distinct
// but the control set is shared (prompt Part 2/3).
export const QuestionField = ({ id, controlType, options, value, disabled, onChange }: TProps) => {
  // Text controls commit on blur so autosave/audit is not triggered per keystroke.
  const [text, setText] = useState(asString(value));
  useEffect(() => setText(asString(value)), [value]);

  const referenceOptions =
    controlType === TEMPLATE_FIELD_TYPE.PERSON
      ? MOCK_USER_NAMES
      : controlType === TEMPLATE_FIELD_TYPE.ENTITY_REFERENCE || controlType === TEMPLATE_FIELD_TYPE.EVIDENCE_REFERENCE
        ? MOCK_DOCUMENT_LABELS
        : [];

  switch (controlType) {
    case TEMPLATE_FIELD_TYPE.YES_NO_UNKNOWN:
      return (
        <RadioGroup
          layout="horizontal"
          value={asString(value)}
          disabled={disabled}
          onChange={(_, data) => onChange(data.value)}
        >
          {YES_NO_UNKNOWN.map((option) => (
            <Radio key={option} value={option} label={option} />
          ))}
        </RadioGroup>
      );

    case TEMPLATE_FIELD_TYPE.SINGLE_CHOICE: {
      const list = options ?? [];
      if (list.length > 0 && list.length <= 4) {
        return (
          <RadioGroup
            layout="horizontal"
            value={asString(value)}
            disabled={disabled}
            onChange={(_, data) => onChange(data.value)}
          >
            {list.map((option) => (
              <Radio key={option} value={option} label={option} />
            ))}
          </RadioGroup>
        );
      }
      return (
        <Dropdown
          selectedOptions={[asString(value)]}
          value={asString(value)}
          disabled={disabled}
          placeholder="Select an option"
          onOptionSelect={(_, data) => onChange(data.optionValue ?? '')}
        >
          {list.map((option) => (
            <Option key={option} value={option} text={option}>
              {option}
            </Option>
          ))}
        </Dropdown>
      );
    }

    case TEMPLATE_FIELD_TYPE.MULTI_SELECT: {
      const selected = asStringArray(value);
      const list = options ?? [];
      return (
        <Dropdown
          multiselect
          selectedOptions={selected}
          value={selected.join(', ')}
          disabled={disabled}
          placeholder="Select all that apply"
          onOptionSelect={(_, data) => onChange(data.selectedOptions)}
        >
          {list.map((option) => (
            <Option key={option} value={option} text={option}>
              {option}
            </Option>
          ))}
        </Dropdown>
      );
    }

    case TEMPLATE_FIELD_TYPE.SHORT_TEXT:
      return (
        <Input
          value={text}
          disabled={disabled}
          onChange={(_, data) => setText(data.value)}
          onBlur={() => onChange(text)}
          placeholder="Enter a value"
        />
      );

    case TEMPLATE_FIELD_TYPE.LONG_TEXT:
      return (
        <Textarea
          value={text}
          disabled={disabled}
          resize="vertical"
          onChange={(_, data) => setText(data.value)}
          onBlur={() => onChange(text)}
          placeholder="Enter details"
        />
      );

    case TEMPLATE_FIELD_TYPE.DATE:
      return (
        <DatePicker
          value={value && typeof value === 'string' ? new Date(value) : null}
          disabled={disabled}
          firstDayOfWeek={1}
          formatDate={(date) => DateLabel({ children: date?.toISOString() || '', short: true })}
          placeholder="Select a date"
          onSelectDate={(date) => onChange(date ? date.toISOString() : null)}
        />
      );

    case TEMPLATE_FIELD_TYPE.PERSON:
    case TEMPLATE_FIELD_TYPE.ENTITY_REFERENCE:
    case TEMPLATE_FIELD_TYPE.EVIDENCE_REFERENCE:
      return (
        <Dropdown
          selectedOptions={[asString(value)]}
          value={asString(value)}
          disabled={disabled}
          placeholder={controlType === TEMPLATE_FIELD_TYPE.PERSON ? 'Select a person or function' : 'Link a reference'}
          onOptionSelect={(_, data) => onChange(data.optionValue ?? '')}
          aria-labelledby={id}
        >
          {referenceOptions.map((option) => (
            <Option key={option} value={option} text={option}>
              {option}
            </Option>
          ))}
        </Dropdown>
      );

    default:
      return null;
  }
};
